import classNames from 'classnames';

import { TEXT_VARIANT_DEFAULTS, TEXT_VARIANT_OPTIONS } from '../../styles/generated/data/types';
import { Responsive, responsiveClasses, withBaseDefault } from '../helpers/responsive';

// The typography model behind RCText: the canonical `{variant}-{size}[-{weight}]` union
// and the pure functions that map a variant to its `text-*` utility classes. No React
// here — the RCText components, the drift-guard test, and Storybook all import from this
// module.
//
// The size/weight/tag vocabulary itself is GENERATED from library/presets/text.presets.json,
// the same source that emits the Sass maps `mixins/rc-text.scss` validates against. The generated
// names are source-scoped, so they're aliased to shorter local names and re-exported — this stays
// the one import site for the model.
//
// Boundary: the generator emits the values; the type-level derivation below
// (`CanonicalTextStyle`, `NonDefaultWeight`) is hand-authored on top of them.
export const propOptions = TEXT_VARIANT_OPTIONS;
export const propDefaults = TEXT_VARIANT_DEFAULTS;

export type TextVariant = keyof typeof propOptions;

// The pickable text colors — every value maps to a `fg-color-text-{value}` utility class:
//  - fixed semantic colors (`secondary`, `accent`, …) resolve to their color token.
//  - `themed` is the surface-tracking color: inside a themed surface (RCThemedBox /
//    RCCallout / `.themed-box`) it takes the surface's family color; elsewhere it falls
//    back to the body text color. It's the one value that isn't a fixed token.
// Tightened to directly-pickable values — the ~30 themed-surface plumbing tokens, the
// accent *scale* (large-text-only), and `link` are intentionally excluded and reached
// via `className` / actual links. Class-based (never inline style) so color joins the
// cascade with defined precedence instead of clobbering themed surfaces.
export const TEXT_COLORS = [
  'primary',
  'secondary',
  'tertiary',
  'discount',
  'accent',
  'success',
  'urgent',
  'info',
  'loud',
  'primary-inverted',
  'primary-on-dark',
  'primary-on-light',
  'primary-disabled',
  'themed',
] as const;

export type TextColor = typeof TEXT_COLORS[number];

// A color value → its foreground utility class on the shared `fg-color-*` family. Every
// value (including `themed`) maps uniformly to `.fg-color-text-{value}` — RCText is body
// text, so it draws from the text tier; the class decides whether that points at a fixed
// color token or the theme-tracking var. `text-color-*` remains as a deprecated alias for
// legacy / CMS consumers.
export function colorToClass(color: TextColor): string {
  return `fg-color-text-${color}`;
}

// The default weight needs no modifier class (the base `text-{variant}-{size}`
// already carries it), so excluding it from the canonical union keeps redundant
// tokens like `title-900-bold` unrepresentable and the union tight (~48, not ~66).
type NonDefaultWeight<V extends TextVariant> = Exclude<
  typeof propOptions[V]['weights'][number],
  typeof propDefaults[V]['weight']
>;

/**
 * Every valid `{variant}-{size}[-{weight}]` typography token, derived from
 * `propOptions` / `propDefaults` so the type can't drift from the emitted `text-*`
 * utility classes. Weight is folded into the token (rather than an independent prop)
 * because weight validity is variant-coupled — `utility-400-semibold` is valid,
 * `title-900-semibold` is not — so folding makes invalid combos unrepresentable.
 */
export type CanonicalTextStyle = {
  [V in TextVariant]:
  | `${V}-${typeof propOptions[V]['sizes'][number]}`
  | `${V}-${typeof propOptions[V]['sizes'][number]}-${NonDefaultWeight<V>}`;
}[TextVariant];

/**
 * Runtime mirror of `CanonicalTextStyle` (a type has no runtime presence). Generated
 * from the same maps, so it stays in lock-step with the union; the drift-guard test
 * asserts every entry maps to a class that exists in the built stylesheet.
 */
export const CANONICAL_TEXT_STYLES: CanonicalTextStyle[] = (Object.keys(propOptions) as TextVariant[])
  .flatMap((variant) =>
    propOptions[variant].sizes.flatMap((size) => {
      const base = `${variant}-${size}` as CanonicalTextStyle;
      const weighted = propOptions[variant].weights
        .filter((weight) => weight !== propDefaults[variant].weight)
        .map((weight) => `${variant}-${size}-${weight}` as CanonicalTextStyle);
      return [base, ...weighted];
    }),
  );

// A canonical token → its utility class(es) at one breakpoint (`prefix` is `''` for
// the desktop base and `tablet-` / `mobile-` otherwise). A `-{weight}` suffix (present
// only for non-default weights) adds the variant-scoped `text-{variant}--{weight}`
// modifier — the only selector specific enough to override the base class's weight
// (a generic `weight-*` utility loses to `text-*` on specificity/source order).
export function variantToClasses(token: CanonicalTextStyle, prefix: string): string {
  const [variant, size, weight] = token.split('-');
  const base = `${prefix}text-${variant}-${size}`;
  return weight ? `${base} ${prefix}text-${variant}--${weight}` : base;
}

// Combines the (already-computed) text-style classes with the variant-independent
// modifiers (color, truncation) and the consumer `className` passthrough.
export function modifierClasses(
  textClasses: string[],
  maxLines: number | undefined,
  color: TextColor | undefined,
  className: string | undefined,
): string {
  return classNames(
    textClasses,
    color !== undefined && colorToClass(color),
    { [`text-truncate-${maxLines}`]: maxLines !== undefined },
    className,
  );
}

// Convenience wrapper for the subcomponents. `size` and `weight` are each responsive (a
// value or a `{ desktop, tablet, mobile }` object) and emitted as two INDEPENDENT axes:
// size → `{prefix}text-{variant}-{size}`, weight → `{prefix}text-{variant}--{weight}`.
// They compose without a combined token because the weight modifier compiles to
// `[class*='text-{variant}'].{prefix}text-{variant}--{weight}` (specificity 0,2,0), which
// always beats the size class's 0,1,0 default weight — so a size change at one breakpoint
// never resets a weight set at another.
//
// `withBaseDefault` injects the variant's defaults at the desktop tier for a partial object
// (e.g. `{ mobile: … }`) so the base keeps a defined style above the breakpoint. The size
// class already carries the default weight, so at the base tier the default weight needs no
// modifier; at a narrower breakpoint the default modifier IS emitted, to override a
// non-default weight set at a wider one (absence wouldn't undo it).
export function getClassnames(
  variant: TextVariant,
  size: Responsive<string>,
  weight: Responsive<string>,
  maxLines: number | undefined,
  color?: TextColor,
  className?: string,
): string {
  const { size: defaultSize, weight: defaultWeight } = propDefaults[variant];

  const sizeClasses = responsiveClasses(
    withBaseDefault(size, defaultSize),
    (value, prefix) => `${prefix}text-${variant}-${value}`,
  );

  const weightClasses = responsiveClasses(
    withBaseDefault(weight, defaultWeight),
    (value, prefix) => (!prefix && value === defaultWeight ? false : `${prefix}text-${variant}--${value}`),
  );

  return modifierClasses([...sizeClasses, ...weightClasses], maxLines, color, className);
}
